import { useRef, useState } from 'react';
import { Globe } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/api';
import { certExpiryLabel, domainErrorMessage, readyEndpoints, TLS_MODE_LABELS } from '@/lib/domainForm';
import { usePolledData } from '@/lib/hooks/usePolledData';
import { cn } from '@/lib/utils';
import type { DomainDetail } from '@/types/domain';
import { DomainDialog, type DomainDialogTarget } from './DomainDialog';
import { OriginCertCard } from './OriginCertCard';

// The table shows detail fields (certificate, readiness), so every domain's detail is read; there are few.
const fetchDomains = async (): Promise<DomainDetail[]> => {
  const list = await api.domains.list();
  return Promise.all(list.map((d) => api.domains.get(d.id)));
};

const DEFAULT_DELETE_HINT = "The default domain can't be deleted. Make another domain the default first.";

const muted = <span className="text-muted-foreground">—</span>;

/** Settings → Domains: the Cloudflare zones apps live under, each with its own origin certificate. */
export function DomainsPanel() {
  const domains = usePolledData(fetchDomains, 30_000);
  const [formTarget, setFormTarget] = useState<DomainDialogTarget>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [defaultBusy, setDefaultBusy] = useState<number | null>(null);
  // `deleting` keeps the target through the dialog's exit animation; `deleteOpen` controls visibility.
  const [deleting, setDeleting] = useState<DomainDetail | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteBusyRef = useRef(false);
  const certRef = useRef<HTMLDivElement>(null);

  const rows = domains.data ?? [];
  const selected = rows.find((d) => d.id === selectedId) ?? rows.find((d) => d.is_default) ?? rows[0] ?? null;
  const { refetch } = domains;

  const setDefault = async (d: DomainDetail) => {
    if (defaultBusy !== null) return;
    setDefaultBusy(d.id);
    try {
      await api.domains.update(d.id, { is_default: true });
      toast.success(`${d.name} is now the default domain`);
    } catch (err) {
      toast.error(domainErrorMessage(err, 'Failed to set the default domain'));
    } finally {
      setDefaultBusy(null);
      refetch();
    }
  };

  const runDelete = async () => {
    const target = deleting;
    if (!target || deleteBusyRef.current) return;
    deleteBusyRef.current = true;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.domains.delete(target.id);
      setDeleteOpen(false);
      toast.success(`Domain ${target.name} deleted`);
      refetch();
    } catch (err) {
      setDeleteError(domainErrorMessage(err, 'Failed to delete domain'));
    } finally {
      deleteBusyRef.current = false;
      setDeleteBusy(false);
    }
  };

  const showCertificate = (d: DomainDetail) => {
    setSelectedId(d.id);
    requestAnimationFrame(() => {
      certRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      certRef.current?.focus({ preventScroll: true });
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <CardTitle>Domains</CardTitle>
              <CardDescription>
                Cloudflare zones apps get hostnames under. Apps deployed without a domain use the default one.
              </CardDescription>
            </div>
            <Button onClick={() => setFormTarget({ mode: 'create' })}>Add domain</Button>
          </div>
        </CardHeader>
        <CardContent data-focus-fallback tabIndex={-1} className="focus:outline-none">
          {domains.loading ? (
            <p role="status" className="py-8 text-center text-muted-foreground">
              Loading domains…
            </p>
          ) : domains.data === undefined ? (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
              <span>{domains.error ?? 'Failed to load domains'}</span>
              <Button size="sm" variant="outline" onClick={refetch}>
                Retry
              </Button>
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              as="h3"
              icon={Globe}
              title="No domains"
              description="Add the Cloudflare zone your apps should get hostnames under. The first domain becomes the default."
            />
          ) : (
            <>
              {domains.error ? (
                <p role="alert" className="mb-4 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                  Could not refresh domains: {domains.error}
                </p>
              ) : null}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Domain</TableHead>
                    <TableHead>New apps</TableHead>
                    <TableHead>Let's Encrypt</TableHead>
                    <TableHead>Origin certificate</TableHead>
                    <TableHead>Ready endpoints</TableHead>
                    <TableHead className="text-right">Apps</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((d) => {
                    const expiry = certExpiryLabel(d.certificate);
                    const { ready, total } = readyEndpoints(d);
                    const isSelected = selected?.id === d.id;
                    return (
                      <TableRow key={d.id} data-state={isSelected ? 'selected' : undefined}>
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-mono text-sm font-medium [overflow-wrap:anywhere]">{d.name}</span>
                            {d.is_default ? <Badge variant="secondary">Default</Badge> : null}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{TLS_MODE_LABELS[d.new_app_tls_mode] ?? d.new_app_tls_mode}</TableCell>
                        <TableCell className="text-sm">
                          {d.letsencrypt_capable ? 'LE available' : <span className="text-muted-foreground">Origin only</span>}
                        </TableCell>
                        <TableCell className="text-sm">
                          {expiry ? (
                            <span className={cn(d.certificate && d.certificate.expires_in_days < 30 && 'font-medium text-status-danger')}>{expiry}</span>
                          ) : (
                            <span className="text-muted-foreground">None</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm tabular-nums">
                          {d.certificate ? (
                            <span className={cn(ready < total && 'font-medium text-status-warning')}>
                              {ready}/{total}
                            </span>
                          ) : (
                            muted
                          )}
                        </TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{d.app_count}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-wrap justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              aria-label={`Certificate for ${d.name}`}
                              aria-pressed={isSelected}
                              onClick={() => showCertificate(d)}
                            >
                              Certificate
                            </Button>
                            <Button size="sm" variant="outline" aria-label={`Edit ${d.name}`} onClick={() => setFormTarget({ mode: 'edit', domain: d })}>
                              Edit
                            </Button>
                            {d.is_default ? null : (
                              <Button
                                size="sm"
                                variant="outline"
                                aria-label={`Make ${d.name} the default domain`}
                                aria-disabled={defaultBusy !== null}
                                onClick={() => void setDefault(d)}
                              >
                                {defaultBusy === d.id ? 'Saving…' : 'Set default'}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="destructive"
                              aria-label={`Delete ${d.name}`}
                              // aria-disabled keeps it focusable so the reason below is announced.
                              aria-disabled={d.is_default}
                              aria-describedby={d.is_default ? `domain-${d.id}-delete-hint` : undefined}
                              title={d.is_default ? DEFAULT_DELETE_HINT : undefined}
                              onClick={() => {
                                if (d.is_default) return;
                                setDeleteError(null);
                                setDeleting(d);
                                setDeleteOpen(true);
                              }}
                            >
                              Delete
                            </Button>
                            {d.is_default ? (
                              <span id={`domain-${d.id}-delete-hint`} className="sr-only">
                                {DEFAULT_DELETE_HINT}
                              </span>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>

      {selected ? (
        <div ref={certRef} tabIndex={-1} className="scroll-mt-4 outline-none" aria-label={`Certificate for ${selected.name}`} role="region">
          <OriginCertCard
            key={selected.id}
            domain={selected}
            onChanged={refetch}
            onRefresh={refetch}
            refreshing={domains.refreshing}
          />
        </div>
      ) : null}

      <DomainDialog
        target={formTarget}
        firstDomain={domains.data !== undefined && rows.length === 0}
        onClose={() => setFormTarget(null)}
        onSaved={(saved) => {
          setFormTarget(null);
          setSelectedId(saved.id);
          refetch();
        }}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(next) => {
          if (!next && !deleteBusyRef.current) {
            setDeleteOpen(false);
            setDeleteError(null);
          }
        }}
        title="Delete domain?"
        description={
          deleting
            ? `Delete ${deleting.name}? Only domains no app uses can be deleted. Its origin certificate is removed from the endpoints.`
            : undefined
        }
        confirmLabel={deleteBusy ? 'Deleting…' : 'Delete'}
        destructive
        loading={deleteBusy}
        error={deleteError}
        onConfirm={() => void runDelete()}
      />
    </div>
  );
}
